<template>
  <Container judul="Galeri">
    <template #header>
      <HeaderFe />
    </template>
    <template v-slot:default>
      <main>
        <section
          class="pb-10"
          v-if="galleries.data && galleries.data.length > 0"
        >
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <h2
              class="mt-6 mb-6 font-semibold sm:mt-8 lg:mt-10 text-heading-5 text-dark"
            >
              Galeri Kegiatan
            </h2>
            <div class="pt-4 border-t border-gray-3">
              <div
                class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 lg:gap-8"
              >
                <div
                  v-for="gallery in galleries.data"
                  :key="gallery.id"
                  class="flex flex-col h-full p-3 transition-all duration-300 bg-white border border-gray-100 shadow-lg sm:p-4 lg:p-5 group rounded-xl hover:shadow-2xl hover:-translate-y-1"
                >
                  <div
                    class="mb-4 sm:mb-5 lg:mb-6 overflow-hidden rounded-[10px] transition-all duration-300 group-hover:scale-105"
                  >
                    <a
                      :href="gallery.link || '#'"
                      @click.prevent="openGallery(gallery)"
                    >
                      <img
                        :src="
                          Array.isArray(gallery.image_path)
                            ? gallery.image_path[0]
                            : gallery.image_path
                        "
                        :alt="gallery.title"
                        class="object-cover w-full h-48 sm:h-52 lg:h-56"
                      />
                    </a>
                  </div>

                  <h4 class="flex-shrink-0">
                    <a
                      :href="gallery.link || '#'"
                      class="block mb-2 text-lg font-bold text-dark sm:text-xl sm:mb-3 mobile-font"
                    >
                      <span
                        class="bg-gradient-to-r from-primary/50 to-primary/40 bg-[length:0px_10px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 hover:bg-[length:100%_3px] group-hover:bg-[length:100%_10px]"
                      >
                        {{ gallery.title }}
                      </span>
                    </a>
                  </h4>
                  <p
                    class="flex-grow text-sm text-gray-600 sm:text-base line-clamp-3 mobile-font"
                  >
                    {{ gallery.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Empty state when no galleries -->
        <section class="pb-10" v-else>
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <h2
              class="mt-6 mb-6 font-semibold sm:mt-8 lg:mt-10 text-heading-5 text-dark"
            >
              Galeri Kegiatan
            </h2>
            <div class="pt-12 border-t border-gray-3">
              <div
                class="flex flex-col items-center justify-center py-12 text-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="w-16 h-16 mb-4 text-gray-400"
                >
                  <path
                    fill-rule="evenodd"
                    d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z"
                    clip-rule="evenodd"
                  />
                </svg>
                <h3 class="mb-2 text-lg font-semibold text-gray-900">
                  Galeri belum tersedia
                </h3>
                <p class="text-gray-500">
                  Belum ada galeri kegiatan yang tersedia saat ini.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { defineProps, ref } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import FooterFe from "@/Components/FooterFe.vue";
import GLightbox from "glightbox";
import "glightbox/dist/css/glightbox.min.css";

let glightboxInstance = null;

defineProps({
  galleries: {
    type: Object,
    required: true,
  },
});

const openGallery = (gallery) => {
  if (!Array.isArray(gallery.image_path) || gallery.image_path.length === 0) {
    alert("Galeri ini tidak memiliki gambar.");
    return;
  }

  const galleryItems = gallery.image_path.map((img) => ({
    href: img,
    type: "image",
    title: `<h4>${gallery.title}</h4><p>${gallery.description}</p>`,
  }));

  if (glightboxInstance) {
    glightboxInstance.destroy();
  }

  glightboxInstance = GLightbox({
    elements: galleryItems,
    touchNavigation: true,
    loop: true,
    zoomable: true,
    autoplayVideos: false,
  });

  glightboxInstance.open();
};
</script>

<style scoped>
/* Line clamp utility for text truncation */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Ensure consistent card heights */
.group {
  min-height: 300px;
}

@media (min-width: 640px) {
  .group {
    min-height: 320px;
  }
}

@media (min-width: 1024px) {
  .group {
    min-height: 350px;
  }
}

/* Mobile-specific font styling */
.mobile-font {
  /* Mobile font (default) */
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
  font-weight: 500;
}

/* Desktop font styling */
@media (min-width: 640px) {
  .mobile-font {
    font-family: "Roboto", "Segoe UI", -apple-system, BlinkMacSystemFont,
      sans-serif;
    font-weight: inherit;
  }
}

/* Smooth hover animations */
.group:hover img {
  transform: scale(1.05);
}
</style>
